import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import clsx from "clsx";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge, Chip, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import StorefrontIcon from "@material-ui/icons/Storefront";
import useSuperadminFeatures from "../hooks/useSuperadminFeatures";
import StyleIcon from "@material-ui/icons/Style";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import BusinessIcon from "@material-ui/icons/Business";
import AssessmentIcon from "@material-ui/icons/Assessment";
import SignalCellularAltIcon from "@material-ui/icons/SignalCellularAlt";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import ListIcon from "@material-ui/icons/List";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import CodeIcon from "@material-ui/icons/Code";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import FlagIcon from "@material-ui/icons/Flag";
import EventIcon from "@material-ui/icons/Event";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import HistoryIcon from "@material-ui/icons/History";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import HomeWorkIcon from "@material-ui/icons/HomeWork";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import BarChartIcon from "@material-ui/icons/BarChart";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";
import { useCrmStatus } from "../hooks/useCrmBoard";
import api from "../services/api";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import useTurnosChatAccess from "../hooks/useTurnosChatAccess";
import useWaiaEnviosMode from "../hooks/useWaiaEnviosMode";
import { Can } from "../components/Can";
import { SettingsContext } from "../context/Settings/SettingsContext";
import ModuleErrorBoundary from "../components/ModuleErrorBoundary";
import useSupportAccess from "../hooks/useSupportAccess";
import useSupportStaffCounter from "../hooks/useSupportStaffCounter";
import useSupportUnread from "../hooks/useSupportUnread";
import ContactSupportOutlinedIcon from "@material-ui/icons/ContactSupportOutlined";

const useStyles = makeStyles(theme => ({
  sidebarTooltip: {
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: theme.spacing(0.75, 1.25),
    marginLeft: theme.spacing(-0.5),
  },
  planChipWrapper: {
    padding: theme.spacing(1, 2, 1, 2),
  },
  planChip: {},
  listItemActive: {
    backgroundColor: fade(theme.palette.primary.main, 0.12),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2) - 3,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    "&:hover": {
      backgroundColor: fade(theme.palette.primary.main, 0.18),
    },
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, className, activeClass, active, showTooltip, tooltipClasses } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const item = (
    <ListItem
      button
      component={renderLink}
      className={clsx(className, active && activeClass)}
    >
      {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
      <ListItemText primary={primary} />
    </ListItem>
  );

  return (
    <li>
      {showTooltip ? (
        <Tooltip title={primary} placement="right" arrow classes={tooltipClasses}>
          {item}
        </Tooltip>
      ) : (
        item
      )}
    </li>
  );
}

// ⚠ El menú se renderiza en TODAS las pantallas. Si el componente que arma la
// entrada de TurnosIA tira, se lleva WAIA entera aunque el usuario no haya
// entrado nunca al módulo — que es exactamente lo que pasó el 24-sep.
//
// Dos reglas, las dos necesarias:
//  1. La entrada NO depende de datos de TurnosIA: solo del flag de la empresa
//     (`turnosIAEnabled`), que ya vive en los settings de WAIA. Nada de pedirle
//     nada al backend de turnos para decidir si se dibuja un <li>.
//  2. Va en su propio boundary `silent` (abajo, en el render): si igual falla,
//     desaparece la entrada y el resto del menú sigue.
//
// El `getSetting` va ACÁ ADENTRO y no en el padre a propósito: un valor leído
// en el padre se evalúa ANTES de que el boundary monte, así que el boundary no
// lo podría atajar.
const TurnosIAMenuEntry = ({ isActive, activeClass, showTooltip, tooltipClasses }) => {
  const { getSetting } = useContext(SettingsContext);
  if (getSetting("turnosIAEnabled") !== "true") return null;
  return (
    <ListItemLink
      to="/turnos"
      primary={i18n.t("sidebar.turnos")}
      icon={<CalendarTodayIcon />}
      active={isActive("/turnos")}
      activeClass={activeClass}
      showTooltip={showTooltip}
      tooltipClasses={tooltipClasses}
    />
  );
};

// feat_395 — Tablero CRM. Se dibuja sólo si GET /crm/status dice enabled &&
// hasActiveStates. El fetch vive en un hijo que se MONTA sólo con el setting
// crmBoardEnabled="enabled" (publicKey): las empresas que no lo usan no hacen
// ningún request extra. El setting llega en vivo por el socket "settings".
const CrmMenuEntryInner = ({ settingValue, ...linkProps }) => {
  const { available } = useCrmStatus(settingValue);
  if (!available) return null;
  return <ListItemLink {...linkProps} />;
};

const CrmMenuEntry = ({ isActive, activeClass, showTooltip, tooltipClasses }) => {
  const { getSetting } = useContext(SettingsContext);
  const value = getSetting("crmBoardEnabled");
  if (value !== "enabled") return null;
  return (
    <CrmMenuEntryInner
      settingValue={value}
      to="/crm"
      primary={i18n.t("mainDrawer.listItems.crmBoard")}
      icon={<ViewColumnOutlinedIcon />}
      active={isActive("/crm")}
      activeClass={activeClass}
      showTooltip={showTooltip}
      tooltipClasses={tooltipClasses}
    />
  );
};

// feat_404 — Soporte. El flag vive en los settings de la empresa; sin request
// extra. Va en su propio boundary silent (módulo aislado).
const SupportMenuEntry = ({ isActive, activeClass, showTooltip, tooltipClasses }) => {
  const allowed = useSupportAccess();
  const unread = useSupportUnread(allowed);
  if (!allowed) return null;
  return (
    <ListItemLink
      to="/soporte"
      primary={i18n.t("support.menu")}
      icon={
        <Badge badgeContent={unread} color="error" max={99} data-support-unread={unread}>
          <ContactSupportOutlinedIcon />
        </Badge>
      }
      active={isActive("/soporte")}
      activeClass={activeClass}
      showTooltip={showTooltip}
      tooltipClasses={tooltipClasses}
    />
  );
};

// feat_407 — Bandeja de Atención del staff de Soporte, con insignia de pendientes.
// Se monta SOLO con la feature `soporte` (el padre gatea con canShow) → los demás
// superadmins no hacen ningún request extra. Boundary silent: si falla, no rompe el menú.
const SupportStaffMenuEntry = ({ isActive, activeClass, showTooltip, tooltipClasses }) => {
  const { pending, late, unreviewed } = useSupportStaffCounter(true);
  const total = pending + unreviewed;
  return (
    <ListItemLink
      to="/superadmin/atencion"
      primary="Atención"
      icon={
        <Badge badgeContent={total} color={late > 0 ? "error" : "primary"} max={99} data-support-badge={total}>
          <QuestionAnswerOutlinedIcon />
        </Badge>
      }
      active={isActive("/superadmin/atencion")}
      activeClass={activeClass}
      showTooltip={showTooltip}
      tooltipClasses={tooltipClasses}
    />
  );
};

const PLAN_COLORS = { basic: "default", pro: "primary", full: "secondary" };

const MainListItems = (props) => {
  const { drawerClose, drawerOpen } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);
  const turnosChat = useTurnosChatAccess(user);
  const waiaEnviosMode = useWaiaEnviosMode(user);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [hasDataForms, setHasDataForms] = useState(false);
  const [matchPropEnabled, setMatchPropEnabled] = useState(false);
  const [aiServicesEnabled, setAiServicesEnabled] = useState(false);
  const [aiStatsEnabled, setAiStatsEnabled] = useState(false);
  const classes = useStyles();
  const location = useLocation();
  const tooltipClasses = React.useMemo(() => ({ tooltip: classes.sidebarTooltip }), [classes.sidebarTooltip]);

  // feat_237: sidebar dinámico por features. Super superadmin ve todo (client-side,
  // sin depender de la API); un reseller ve solo sus features permitidas.
  const { has: hasFeature } = useSuperadminFeatures();
  const canShow = (key) => !user.whitelabelScopeId || hasFeature(key);

  const isActive = (to) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  useEffect(() => {
    const isCompanyAdmin = user.profile === "admin" && !user.isSuperAdmin;
    // feat_333: en modo WAIAenvios "Carga de Datos" es UNO DE LOS DOS ítems del
    // menú. Si hasDataForms solo se calculara para admin, un usuario no-admin de
    // una empresa WAIAenvios no vería NUNCA el ítem y el modo quedaría con uno
    // solo. Se amplía SOLO esta llamada; MatchProp / ServiciosIA / stats siguen
    // siendo admin-only porque sus ítems ni se renderizan en este modo.
    // ⚠ MEDIDO 2026-09-04, no supuesto: la ruta GET /company-data-forms es
    // `isAuth` a secas, pero el CONTROLADOR corta con 403 ERR_NO_PERMISSION si
    // profile !== "admin" (CompanyDataFormController.listForCompany). Lo mismo
    // vale para todo bulk_send (BulkSendController.requireAdmin, 10 call sites)
    // y para el access-log. Es decir: hoy WAIAenvios EXIGE que el usuario del
    // cliente sea `admin` de su empresa. Este widening deja el frontend listo y
    // es inocuo (el 403 cae en el .catch), pero abrir el modo a no-admins es una
    // decisión de fase 2 → todo_057.
    if (isCompanyAdmin || waiaEnviosMode) {
      api.get("/company-data-forms")
        .then(({ data }) => setHasDataForms(Array.isArray(data) && data.length > 0))
        .catch(() => {});
    }
    if (isCompanyAdmin) {
      // MatchProp: el ítem solo aparece si superadmin habilitó la capability.
      api.get("/matchprop/admin/config")
        .then(({ data }) => setMatchPropEnabled(!!data?.enabled))
        .catch(() => {});
      // ServiciosIA: ídem — el ítem aparece solo si superadmin habilitó "ai".
      api.get("/ai/admin/config")
        .then(({ data }) => setAiServicesEnabled(!!data?.enabled))
        .catch(() => {});
      // Stats por asistente IA: el ítem aparece solo si superadmin habilitó el
      // módulo (con o sin datos; la página muestra su estado vacío si no hay).
      api.get("/ai-assistant-stats/config")
        .then(({ data }) => setAiStatsEnabled(!!data?.enabled))
        .catch(() => {});
    }
  }, [user.profile, user.isSuperAdmin, waiaEnviosMode]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // Superadmin: unified "Panel general" (feat_231) — absorbe Empresas + Dashboard
  if (user.isSuperAdmin) {
    return (
      <div onClick={drawerClose}>
        <Divider />
        <ListSubheader inset>Superadmin</ListSubheader>
        <ListItemLink
          to="/superadmin"
          primary={i18n.t("panel.title")}
          icon={<DashboardOutlinedIcon />}
          active={location.pathname === "/superadmin"}
          activeClass={classes.listItemActive}
          showTooltip={!drawerOpen}
          tooltipClasses={tooltipClasses}
        />
        {/* feat_236: gestión de superadmins + panel resellers solo para el
            super superadmin (whitelabelScopeId NULL); un reseller no los ve. */}
        {/* feat_237: cada ítem gateado por feature. canShow: super superadmin ve
            todo (client-side), reseller solo sus features. */}
        {canShow("superadmins") && (
          <ListItemLink
            to="/superadmin/users"
            primary="Superadmins"
            icon={<SupervisorAccountIcon />}
            active={isActive("/superadmin/users")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("resellers") && (
          <ListItemLink
            to="/superadmin/resellers"
            primary="Resellers"
            icon={<StorefrontIcon />}
            active={isActive("/superadmin/resellers")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("connections") && (
          <ListItemLink
            to="/superadmin/connections"
            primary={i18n.t("sidebar.connectionsOverview")}
            icon={<SignalCellularAltIcon />}
            active={isActive("/superadmin/connections")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("logs") && (
          <ListItemLink
            to="/superadmin/logs"
            primary={i18n.t("sidebar.logsDelSistema")}
            icon={<ListIcon />}
            active={isActive("/superadmin/logs")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("announcements") && (
          <ListItemLink
            to="/superadmin/announcements"
            primary={i18n.t("sidebar.avisos")}
            icon={<AnnouncementIcon />}
            active={isActive("/superadmin/announcements")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("apis") && (
          <ListItemLink
            to="/superadmin/apis"
            primary={i18n.t("sidebar.apis")}
            icon={<CodeIcon />}
            active={isActive("/superadmin/apis")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("citas") && (
          <ListItemLink
            to="/appointments-settings"
            primary={i18n.t("sidebar.citas")}
            icon={<EventIcon />}
            active={isActive("/appointments-settings")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("changelog") && (
          <ListItemLink
            to="/superadmin/changelog"
            primary={i18n.t("sidebar.changelog")}
            icon={<HistoryIcon />}
            active={isActive("/superadmin/changelog")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("docs") && (
          <ListItemLink
            to="/superadmin/docs"
            primary="Features & Pendientes"
            icon={<LibraryBooksIcon />}
            active={isActive("/superadmin/docs")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("whitelabel") && (
          <ListItemLink
            to="/superadmin/whitelabel"
            primary="Marca Blanca"
            icon={<StyleIcon />}
            active={isActive("/superadmin/whitelabel")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("matchprop") && (
          <ListItemLink
            to="/superadmin/matchprop"
            primary={i18n.t("sidebar.matchprop")}
            icon={<HomeWorkIcon />}
            active={isActive("/superadmin/matchprop")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("ai") && (
          <ListItemLink
            to="/superadmin/ai"
            primary={i18n.t("sidebar.aiServices")}
            icon={<RecordVoiceOverIcon />}
            active={isActive("/superadmin/ai")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("ai_stats") && (
          <ListItemLink
            to="/superadmin/ai-stats"
            primary={i18n.t("sidebar.aiStats")}
            icon={<BarChartIcon />}
            active={isActive("/superadmin/ai-stats")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {canShow("turnosIA") && (
          <ListItemLink
            to="/superadmin/turnosIA"
            primary={i18n.t("sidebar.turnosIA")}
            icon={<CalendarTodayIcon />}
            active={isActive("/superadmin/turnosIA")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {/* feat_407: Bandeja de Atención (Soporte). Español hardcodeado (solo superadmin). */}
        {canShow("soporte") && (
          <ModuleErrorBoundary module="Soporte/staff-menu" variant="silent">
            <SupportStaffMenuEntry
              isActive={isActive}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
          </ModuleErrorBoundary>
        )}
        {/* feat_296: checklist interno del equipo. Label en español hardcodeado
            como el resto del panel superadmin (no lo ve ningún cliente). */}
        {canShow("checklist") && (
          <ListItemLink
            to="/superadmin/checklist"
            primary="Checklist"
            icon={<PlaylistAddCheckIcon />}
            active={isActive("/superadmin/checklist")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
      </div>
    );
  }

  // Profesional / Agendador: solo muestra el ítem de Turnos.
  // En la versión full (con_chat) suma "Chats" para atender las derivaciones humanas.
  if (user.profile === "profesional" || user.profile === "agendador") {
    return (
      <div onClick={drawerClose}>
        <ListItemLink
          to="/turnos"
          primary={i18n.t("sidebar.turnos")}
          icon={<CalendarTodayIcon />}
          active={isActive("/turnos")}
          activeClass={classes.listItemActive}
          showTooltip={!drawerOpen}
          tooltipClasses={tooltipClasses}
        />
        {turnosChat.enabled && (
          <ListItemLink
            to="/tickets"
            primary={i18n.t("mainDrawer.listItems.tickets")}
            icon={<WhatsAppIcon />}
            active={isActive("/tickets")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
      </div>
    );
  }

  // feat_333 — Modo WAIAenvios: panel podado a Conexiones + Carga de Datos.
  // El cliente solo dispara campañas por plantilla; las respuestas las atiende
  // desde su teléfono, así que no hay Chats, Contactos, Campañas, Dashboard ni
  // bloque de administración. El reporte de campañas ya vive DENTRO de Carga de
  // Datos (BulkSendPanel) — por eso no se agrega un ítem "Reportes".
  // ⚠ Orden: va DESPUÉS de superadmin y de profesional/agendador, y ANTES del
  // menú normal. Un superadmin logueado en la empresa sigue viendo el panel
  // completo (useWaiaEnviosMode ya excluye a isSuperAdmin; el orden lo hace
  // explícito). Y profesional/agendador conservan su rama porque Route.js los
  // ancla a /turnos: con el menú de WAIAenvios sus dos links rebotarían.
  if (waiaEnviosMode) {
    return (
      <div onClick={drawerClose}>
        <ListItemLink
          to="/connections"
          primary={i18n.t("mainDrawer.listItems.connections")}
          icon={
            <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
              <SyncAltIcon />
            </Badge>
          }
          active={isActive("/connections")}
          activeClass={classes.listItemActive}
          showTooltip={!drawerOpen}
          tooltipClasses={tooltipClasses}
        />
        {hasDataForms && (
          <ListItemLink
            to="/carga-de-datos"
            primary={i18n.t("cargaDeDatos.menuTitle")}
            icon={<CloudUploadIcon />}
            active={isActive("/carga-de-datos")}
            activeClass={classes.listItemActive}
            showTooltip={!drawerOpen}
            tooltipClasses={tooltipClasses}
          />
        )}
        {user.company && (
          <div className={classes.planChipWrapper}>
            <Tooltip title={`Plan: ${user.company.plan}`} placement="right">
              <Chip
                className={classes.planChip}
                label={(user.company.plan || "BASIC").toUpperCase()}
                color={PLAN_COLORS[user.company.plan] || "default"}
                size="small"
              />
            </Tooltip>
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={drawerClose}>
      {/* 1. Dashboard */}
      <ListItemLink
        to="/dashboard"
        primary={i18n.t("mainDrawer.listItems.dashboard")}
        icon={<AssessmentIcon />}
        active={isActive("/dashboard")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 2. Chats */}
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        active={isActive("/tickets")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 3. Contactos */}
      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        active={isActive("/contacts")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 3b. Tablero CRM (feat_395) */}
      <CrmMenuEntry
        isActive={isActive}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 4. Pendientes */}
      <ListItemLink
        to="/pending-dashboard"
        primary={i18n.t("sidebar.pendientes")}
        icon={<AccessTimeIcon />}
        active={isActive("/pending-dashboard")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 5. Estadísticas */}
      <ListItemLink
        to="/stats"
        primary={i18n.t("sidebar.estadisticas")}
        icon={<AssessmentIcon />}
        active={isActive("/stats")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      <Divider />
      <ListSubheader inset>
        {i18n.t("mainDrawer.listItems.administration")}
      </ListSubheader>
      {/* Estadísticas IA — primero, solo admin + si hay asistentes con datos */}
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() =>
          aiStatsEnabled && (
            <ListItemLink
              to="/ai-assistant-stats"
              primary={i18n.t("aiAssistantStats.menu")}
              icon={<AssessmentIcon />}
              active={isActive("/ai-assistant-stats")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
          )
        }
      />
      {/* 7. Respuestas rápidas */}
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        active={isActive("/quickAnswers")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* 8. Etiquetas */}
      <ListItemLink
        to="/tags"
        primary={i18n.t("sidebar.etiquetas")}
        icon={<LocalOfferIcon />}
        active={isActive("/tags")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            {/* 9. Equipo */}
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              active={isActive("/users")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
            {/* 10. Departamentos */}
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              active={isActive("/queues")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
            {/* 11. Carga de Datos — solo si hay forms habilitados */}
            {hasDataForms && (
              <ListItemLink
                to="/carga-de-datos"
                primary={i18n.t("cargaDeDatos.menuTitle")}
                icon={<CloudUploadIcon />}
                active={isActive("/carga-de-datos")}
                activeClass={classes.listItemActive}
                showTooltip={!drawerOpen}
                tooltipClasses={tooltipClasses}
              />
            )}
            {/* 12. Estados de Contacto */}
            <ListItemLink
              to="/contact-states-admin"
              primary={i18n.t("sidebar.estadosDeContacto")}
              icon={<FlagIcon />}
              active={isActive("/contact-states-admin")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
            {/* 13. Citas */}
            <ListItemLink
              to="/appointments-settings"
              primary={i18n.t("sidebar.citas")}
              icon={<EventIcon />}
              active={isActive("/appointments-settings")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
            {/* 14. MatchProp — solo si superadmin habilitó la capability */}
            {matchPropEnabled && (
              <ListItemLink
                to="/matchprop-admin"
                primary={i18n.t("sidebar.matchprop")}
                icon={<HomeWorkIcon />}
                active={isActive("/matchprop-admin")}
                activeClass={classes.listItemActive}
                showTooltip={!drawerOpen}
                tooltipClasses={tooltipClasses}
              />
            )}
            {/* 15. ServiciosIA — solo si superadmin habilitó la capability "ai" */}
            {aiServicesEnabled && (
              <ListItemLink
                to="/ai-services"
                primary={i18n.t("sidebar.aiServices")}
                icon={<RecordVoiceOverIcon />}
                active={isActive("/ai-services")}
                activeClass={classes.listItemActive}
                showTooltip={!drawerOpen}
                tooltipClasses={tooltipClasses}
              />
            )}
            {/* Soporte (feat_404) — admin con supportEnabled */}
            <ModuleErrorBoundary module="Soporte/menu" variant="silent">
              <SupportMenuEntry
                isActive={isActive}
                activeClass={classes.listItemActive}
                showTooltip={!drawerOpen}
                tooltipClasses={tooltipClasses}
              />
            </ModuleErrorBoundary>
            {/* Configuración — movido al final del sidebar (feat_194) */}
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              active={isActive("/settings")}
              activeClass={classes.listItemActive}
              showTooltip={!drawerOpen}
              tooltipClasses={tooltipClasses}
            />
          </>
        )}
      />
      {/* 14. TurnosIA — solo si está habilitado para la empresa */}
      <ModuleErrorBoundary module="TurnosIA/menu" variant="silent">
        <TurnosIAMenuEntry
          isActive={isActive}
          activeClass={classes.listItemActive}
          showTooltip={!drawerOpen}
          tooltipClasses={tooltipClasses}
        />
      </ModuleErrorBoundary>
      {/* 15. Conexiones */}
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        active={isActive("/connections")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
      {/* Plan Full */}
      {user.company && (
        <div className={classes.planChipWrapper}>
          <Tooltip title={`Plan: ${user.company.plan}`} placement="right">
            <Chip
              className={classes.planChip}
              label={(user.company.plan || "BASIC").toUpperCase()}
              color={PLAN_COLORS[user.company.plan] || "default"}
              size="small"
            />
          </Tooltip>
        </div>
      )}
      <Divider />
      {/* Comunicados — info de sistema, al final */}
      <ListItemLink
        to="/announcements"
        primary={i18n.t("mainDrawer.listItems.announcements")}
        icon={<AnnouncementIcon />}
        active={isActive("/announcements")}
        activeClass={classes.listItemActive}
        showTooltip={!drawerOpen}
        tooltipClasses={tooltipClasses}
      />
    </div>
  );
};

export default MainListItems;
